import { useState } from "react";
import { Link2, ClipboardPaste, Loader2, Eye, PackageSearch, Search } from "lucide-react";
import { verifyLink, deepScan } from "@/lib/api";
import { getTrustBadge, guessManufacturer } from "./dealFeedData";
import { toast } from "@/components/ui/sonner";
import OutOfCreditsModal from "./OutOfCreditsModal";
import { SafeImg } from "@/components/SafeImg";

const ACTION_STYLE = {
  BUY: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  WAIT: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  AVOID: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
};

// Maps backend VERDICT_* codes onto Section 2's shared badge renderer.
function badgeFor(verdictCode) {
  const key = (verdictCode || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", "");
  return getTrustBadge(key);
}

// CHECK viewport — Section 4, the Link Inspector. Mobile: stacked input + verdict
// card. Desktop: split view (domain metadata left, price/verdict analysis right).
export default function CheckView({ active, onOpenUpgrade, onOpenPdp, onResult }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [deepScanLoading, setDeepScanLoading] = useState(false);
  const [altSellers, setAltSellers] = useState(null);
  const [outOfCreditsReset, setOutOfCreditsReset] = useState(null);

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text);
    } catch { /* clipboard permission denied — ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim() || loading) return;
    setLoading(true);
    setError("");
    setAltSellers(null);
    try {
      const res = await verifyLink(url.trim());
      setResult(res.inspection_result);
      onResult?.(res.inspection_result);
    } catch (err) {
      setError(err?.message || "Couldn't inspect that link — try again.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Section 6 — Deep Trust Inspector: a SEPARATE premium action from the free verdict
  // above. FREE tier spends 1 of its 10 Daily Credits; PLUS/PRO/ELITE are unlimited.
  const runDeepScan = async () => {
    if (!r?.product_title || deepScanLoading) return;
    setDeepScanLoading(true);
    try {
      const res = await deepScan({ title: r.product_title, excludeDomain: r.domain });
      setAltSellers(res.alt_sellers || []);
    } catch (err) {
      if (err?.status === 402) setOutOfCreditsReset(err?.data?.reset_at || true);
      else toast.error("Deep Scan is temporarily unavailable — try again shortly.");
    } finally {
      setDeepScanLoading(false);
    }
  };

  const r = result;
  const badge = r ? badgeFor(r.verdict_code) : null;

  return (
    <section id="view-check" data-testid="view-check" className={active ? "tab-view active" : "tab-view hidden"}>
      <form onSubmit={submit} className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 dark:border-white/10 dark:bg-white/5" data-testid="check-link-form">
        <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste any reseller link here (e.g., https://...)"
          data-testid="check-link-input"
          className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-zinc-500"
        />
        <button type="button" onClick={pasteFromClipboard} data-testid="check-link-paste-btn" aria-label="Paste from clipboard" className="shrink-0 rounded-lg border border-slate-300 p-2 text-slate-500 dark:border-white/10 dark:text-zinc-400">
          <ClipboardPaste className="h-4 w-4" />
        </button>
        <button type="submit" disabled={!url.trim() || loading} data-testid="check-link-submit" className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-40 dark:bg-white dark:text-slate-950">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Inspect Link"}
        </button>
      </form>

      {error && <p className="mt-3 text-sm font-bold text-rose-500" data-testid="check-link-error">{error}</p>}

      {r && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 md:items-stretch" data-testid="check-verdict-card">
          {/* Domain metadata panel — desktop left split */}
          <div className="flex h-full flex-col justify-center rounded-2xl border border-slate-200 p-4 dark:border-white/10" data-testid="check-domain-panel">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-zinc-500">Merchant Status</p>
            <p className="mt-1 text-sm font-black text-slate-900 dark:text-white" data-testid="check-domain">{r.domain}</p>
            <p className={`mt-1 text-xs font-bold ${r.is_us_merchant ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500"}`} data-testid="check-us-merchant">
              {r.is_us_merchant ? "US merchant confirmed" : "Not a confirmed US merchant"}
            </p>
            {r.risk_factors?.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs font-bold text-rose-500" data-testid="check-risk-factors">
                {r.risk_factors.map((f) => <li key={f}>⚠ {f}</li>)}
              </ul>
            )}
          </div>

          {/* Price/verdict analysis panel — desktop right split */}
          <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-4 dark:border-white/10" data-testid="check-analysis-panel">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`trust-badge inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-black uppercase tracking-wide ${badge.className}`} data-testid="check-verdict-badge">
                <badge.Icon className="h-3.5 w-3.5" strokeWidth={2.75} /> {badge.label}
              </span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-wide ${ACTION_STYLE[r.action_recommendation]}`} data-testid="check-action-recommendation">
                Action: {r.action_recommendation}
              </span>
            </div>

            <div className="mt-3 flex gap-3">
              {r.product_image ? (
                <SafeImg src={r.product_image} alt={r.product_title} className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 bg-white object-contain dark:border-white/10" iconClassName="h-6 w-6 text-slate-300 dark:text-zinc-600" />
              ) : (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-white/10">
                  <PackageSearch className="h-6 w-6 text-slate-300 dark:text-zinc-600" />
                </div>
              )}
              <h4 className="line-clamp-2 text-sm font-bold text-slate-900 dark:text-white" data-testid="check-product-title">{r.product_title}</h4>
            </div>

            <p className="mt-2 text-sm text-slate-600 dark:text-zinc-300" data-testid="check-price-comparison">
              Pasted Price: {r.scraped_price != null ? `$${r.scraped_price.toFixed(2)}` : "Unavailable"} | True Market Baseline: {r.msrp_baseline != null ? `$${r.msrp_baseline.toFixed(2)}` : "Unavailable"}
            </p>
            {r.discount_percent != null && (
              <p className="mt-1 text-xs font-bold text-brand-orange dark:text-amber-400" data-testid="check-discount-percent">
                {r.discount_percent < 0 ? `Saves ${Math.abs(r.discount_percent)}% off standard MSRP` : `${r.discount_percent}% above standard MSRP`}
              </p>
            )}

            <button
              onClick={() => onOpenPdp?.({
                id: r.domain, title: r.product_title, price: r.scraped_price ?? 0, msrp: r.msrp_baseline ?? r.scraped_price ?? 0,
                image: r.product_image, reseller: r.domain, manufacturer: guessManufacturer(r.product_title || ""),
                verdict: (r.verdict_code || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
                outboundUrl: r.target_url,
                fulfillment: r.fulfillment_mode || "ONLINE_SHIPPING",
                sources: altSellers?.length ? [{ reseller: r.domain, price: r.scraped_price ?? 0 }, ...altSellers.map((s) => ({ reseller: s.reseller, price: s.price }))] : undefined,
              })}
              data-testid="check-review-btn"
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-2.5 text-xs font-black uppercase text-white dark:bg-white dark:text-slate-950"
            >
              <Eye className="h-4 w-4" /> Review Product
            </button>

            {/* Section 6 — Deep Trust Inspector: separate premium action, 1 Daily
                Credit on FREE (unlimited on PLUS/PRO/ELITE); the verdict above stays free. */}
            <button
              onClick={runDeepScan}
              disabled={deepScanLoading}
              data-testid="check-deep-scan-btn"
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-300 py-2.5 text-xs font-black uppercase text-slate-600 disabled:opacity-50 dark:border-white/10 dark:text-zinc-300"
            >
              {deepScanLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Deep Scan Other Sellers (1 Credit)
            </button>

            {altSellers && (
              <div className="mt-3" data-testid="check-deep-scan-results">
                {altSellers.length === 0 ? (
                  <p className="text-xs font-bold text-slate-400 dark:text-zinc-500">No other US sellers found for this product.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {altSellers.map((s, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-bold dark:bg-white/10" data-testid={`check-alt-seller-${i}`}>
                        <span className="truncate text-slate-600 dark:text-zinc-300">{s.reseller}</span>
                        <span className="shrink-0 text-brand-orange dark:text-amber-400">${s.price.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <OutOfCreditsModal
        open={!!outOfCreditsReset}
        onClose={() => setOutOfCreditsReset(null)}
        resetAt={typeof outOfCreditsReset === "string" ? outOfCreditsReset : null}
        onUpgrade={() => { setOutOfCreditsReset(null); onOpenUpgrade?.(); }}
      />
    </section>
  );
}
